import React, { useState, useEffect } from 'react';
import { Card, Form, Switch, Button, Row, Col, Spin, Space, Tooltip, Divider } from 'antd';
import { InfoCircleOutlined, SaveOutlined, ReloadOutlined, SafetyCertificateOutlined, UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import moment from 'moment';

const TerminalAttestationSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  const [form] = Form.useForm();

  const fetchAttestationConfig = async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.terminal}/api/v1/admin/config/attestation`);
      if (res?.success || res?.status) {
        const config = res.data || res;
        form.setFieldsValue({
          attestationEnforce: config.attestationEnforce ?? config.enforce ?? false,
        });
        setLastUpdatedBy(config.updatedBy || '');
        setLastUpdatedAt(config.updatedAt || '');
      } else {
        NotificationManager.error(res?.message || "Failed to load Terminal Attestation config.");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred while loading Terminal Attestation configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttestationConfig();
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        attestationEnforce: !!values.attestationEnforce,
      };

      const res = await httpPut(`${customBaseUrl.terminal}/api/v1/admin/config/attestation`, payload);
      if (res?.success || res?.status || res?.status === "SUCCESS") {
        Swal.fire({
          title: "Configuration Saved",
          text: "Terminal Attestation configuration has been updated successfully.",
          icon: "success",
          confirmButtonColor: "#ff6700"
        });
        fetchAttestationConfig();
      } else {
        NotificationManager.error(res?.message || "Failed to save configuration.");
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error("An error occurred while saving configuration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Loading configurations...">
      <div style={{ padding: '8px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
          <span style={{ color: '#595959', fontSize: '13px' }}>
            Configure terminal attestation enforcement to verify terminal authenticity before processing transactions.
          </span>
          <Button size="small" type="default" icon={<ReloadOutlined />} onClick={fetchAttestationConfig}>
            Reload Config
          </Button>
        </div>

        <Card
          title={
            <Space>
              <SafetyCertificateOutlined style={{ color: '#ff6700' }} />
              <span>Terminal Attestation Security Configuration</span>
            </Space>
          }
          style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            requiredMark={false}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="attestationEnforce"
                  label={
                    <Space>
                      <strong>Enable Attestation Enforcement</strong>
                      <Tooltip title="When enabled, enrolled terminals MUST send valid X-Attestation-* headers on every money-moving call.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(lastUpdatedBy || lastUpdatedAt) ? (
                <div style={{ display: 'flex', gap: 16, color: '#8c8c8c', fontSize: '12px', alignItems: 'center' }}>
                  {lastUpdatedBy && (
                    <span>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Last updated by: <strong>{lastUpdatedBy}</strong>
                    </span>
                  )}
                  {lastUpdatedAt && (
                    <span>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      Updated at: <strong>{moment(lastUpdatedAt).format('YYYY-MM-DD HH:mm:ss')}</strong>
                    </span>
                  )}
                </div>
              ) : <div />}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                style={{
                  background: '#ff6700',
                  borderColor: '#ff6700',
                  height: 40,
                  borderRadius: 6,
                  fontWeight: 600,
                  boxShadow: '0 4px 10px rgba(255, 103, 0, 0.2)'
                }}
              >
                Save Configuration
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </Spin>
  );
};

export default TerminalAttestationSettings;
